import { useThree } from '@react-three/fiber';
import { ManualPromise } from '@zajno/common/async/misc';
import { setTimeoutFramesAsync } from '@zajno/common/async/timeout';
import { createLogger } from 'common/logger';
import { useEffect } from 'react';
import { LazyQueue } from 'scripts/components/lazy/lazyLoadComponent';

type Props = {
    name: string;
    rootRef?: React.MutableRefObject<THREE.Group>;
    manualPromise?: ManualPromise<void>;
    withLazyPriority?: number;
};

const logger = createLogger('[WebGLPreCompiler]');


export const PreCompiler = (props: Props) => {

    const { rootRef, manualPromise, name, withLazyPriority } = props;

    const scene = useThree(state => state.scene);
    const renderer = useThree(state => state.gl);
    const camera = useThree(state => state.camera);

    useEffect(() => {
        compileScene(scene, renderer, camera, name, rootRef, manualPromise, withLazyPriority);
    }, [camera, manualPromise, name, renderer, rootRef, scene, withLazyPriority]);

    // doCompile(scene, renderer, camera, name, rootRef, manualPromise);

    return null;
};

const CompiledMap = new Map<string, boolean>();

const compileScene = (
    scene: THREE.Scene,
    renderer: THREE.WebGLRenderer,
    camera: THREE.Camera,
    name: string,
    rootRef: React.MutableRefObject<THREE.Group>,
    manualPromise: ManualPromise<void>,
    withLazyPriority?: number,
) => {
    if (CompiledMap.get(name)) {
        return;
    }

    if (!scene || !renderer || !camera) {
        logger.warn('missing scene/renderer/camera', scene, renderer, camera);
        return;
    }

    CompiledMap.set(name, true);

    const _doCompile = async () => {
        const now = performance.now();

        let prevVisible: boolean;
        if (rootRef?.current) {
            prevVisible = rootRef.current.visible;
            rootRef.current.visible = true;
        }

        await setTimeoutFramesAsync(10);

        try {
            renderer.compile(scene, camera);
            logger.log('scene compiled:', name, 'in', Math.round(performance.now() - now), 'ms');
        } catch (err) {
            logger.error('Failed to compile scene', name, err);
        }

        if (rootRef?.current) {
            rootRef.current.visible = prevVisible;
        }

        manualPromise?.resolve();
    };

    if (withLazyPriority != null) {
        logger.log('Enqueue', name, 'with priority', withLazyPriority);
        LazyQueue.enqueue(_doCompile, withLazyPriority);
    } else {
        _doCompile();
    }
};
